import { HttpService } from './http.service';
import { Config } from '../config';

export type EntityType = 'fleet' | 'authority';

// Fleet-related config types (excludes authority.config which is a separate entity)
export const FLEET_CONFIG_TYPES = ['fleet.config', 'fleet.enforcement_exceptions', 'fleet.block_mapping', 'fleet.ota_config'] as const;

export type ConfigType =
  | 'authority.config'
  | 'fleet.config'
  | 'fleet.enforcement_exceptions'
  | 'fleet.block_mapping'
  | 'ota.config'
  | 'fleet.ota_config';

export const CONFIG_TYPE_LABELS: Record<ConfigType, string> = {
  'fleet.config': 'Fleet Config',
  'fleet.enforcement_exceptions': 'Enforcement Exceptions',
  'fleet.block_mapping': 'Block Mapping',
  'fleet.ota_config': 'OTA Config',
  'ota.config': 'OTA Config',
  'authority.config': 'Authority Config',
} as const;

export const formatConfigType = (type: ConfigType): string => CONFIG_TYPE_LABELS[type] ?? type;

export type OperationType = 'create' | 'update' | 'delete';

export type ChangeRequestStatus = 'pending' | 'approved' | 'rejected';

export interface CreateChangeRequestPayload {
  entity_id: string;
  config_type: ConfigType;
  operation: OperationType;
  target_object_id?: string | null;
  previous_value?: Record<string, unknown> | null;
  proposed_change?: Record<string, unknown> | null;
  change_reason: string;
}

export interface ChangeRequest {
  id: number;
  entity_id: string;
  config_type: ConfigType;
  operation: OperationType;
  target_object_id?: string | null;
  previous_value?: Record<string, unknown> | null;
  proposed_change?: Record<string, unknown> | null;
  change_reason: string;
  rejection_reason?: string | null;
  change_ticket?: string | null;
  status: ChangeRequestStatus;
  requested_by: string;
  reviewed_by?: string | null;
  created_at: string;
  reviewed_at?: string | null;
}

export interface ChangeRequestsResponse {
  change_requests: ChangeRequest[];
}

export interface ApproveRejectPayload {
  status: 'approved' | 'rejected';
  rejection_reason?: string | null;
}

export abstract class ConfigurationManagementService {
  private static readonly baseUrl = `${Config.API}/config-management-api/v1/config-change-requests`;

  /**
   * Create a new configuration change request
   */
  public static createChangeRequest = (payload: CreateChangeRequestPayload): Promise<ChangeRequest> => {
    return HttpService.fetchAuthorized(ConfigurationManagementService.baseUrl, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  };

  /**
   * List change requests for a specific entity.
   * Returns all change requests - filter by config_type and status on the frontend.
   */
  public static listChangeRequests = (entityType: EntityType, entityId: string): Promise<ChangeRequest[]> => {
    const params = new URLSearchParams({ entity_type: entityType, entity_id: entityId });

    return HttpService.fetchAuthorized(`${ConfigurationManagementService.baseUrl}?${params.toString()}`).then(
      (response: ChangeRequest[]) => response,
    );
  };

  /**
   * Get a single change request by ID
   */
  public static getById = (id: number): Promise<ChangeRequest> => {
    return HttpService.fetchAuthorized(`${ConfigurationManagementService.baseUrl}/${id}`);
  };

  /**
   * Approve or reject a change request
   */
  public static approveOrReject = (id: number, payload: ApproveRejectPayload): Promise<void> => {
    return HttpService.fetchAuthorized(`${ConfigurationManagementService.baseUrl}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  };

  /**
   * Approve a change request
   */
  public static approve = (id: number): Promise<void> => {
    return ConfigurationManagementService.approveOrReject(id, {
      status: 'approved',
    });
  };

  /**
   * Reject a change request
   */
  public static reject = (id: number, rejectionReason?: string): Promise<void> => {
    return ConfigurationManagementService.approveOrReject(id, {
      status: 'rejected',
      rejection_reason: rejectionReason,
    });
  };
}
